import {
	closestRequired,
	currentTargetRequired,
	qsaOptional,
	qsaRequired,
	qsOptional,
	qsRequired,
} from '@/scripts/functions'
import { CountryProvinceSelector, type uCoastWindow } from '@/scripts/setup'
import { UcoastEl } from '@/scripts/core/UcoastEl'

declare let window: uCoastWindow

const selectors = {
	customerAddresses: '[data-customer-addresses]',
	addressCountrySelect: '[data-address-country-select]',
	addressContainer: '[data-address]',
	toggleAddressButton: 'button[aria-expanded]',
	cancelAddressButton: 'button[type="reset"]',
	deleteAddressButton: 'button[data-confirm-message]',
}

const attributes = {
	expanded: 'aria-expanded',
	confirmMessage: 'data-confirm-message',
}

export class CustomerAddresses {
	elements: {
		addressContainer?: HTMLElement
		toggleButtons?: NodeListOf<HTMLElement>
		cancelButtons?: NodeListOf<HTMLElement>
		deleteButtons?: NodeListOf<HTMLElement>
		countrySelects?: NodeListOf<HTMLElement>
	}

	constructor() {
		this.elements = this._getElements()
		this._setupCountries()
		this._setupEventListeners()
	}

	_getElements() {
		const container = qsOptional(selectors.customerAddresses)
		if (!container)
			return {
				container: undefined,
				addressContainer: undefined,
				toggleButtons: undefined,
				cancelButtons: undefined,
				deleteButtons: undefined,
				countrySelects: undefined,
			}
		return {
			container,
			addressContainer: qsOptional(selectors.addressContainer, container),
			toggleButtons: qsaOptional(selectors.toggleAddressButton),
			cancelButtons: qsaOptional(selectors.cancelAddressButton),
			deleteButtons: qsaOptional(selectors.deleteAddressButton),
			countrySelects: qsaOptional(selectors.addressCountrySelect),
		}
	}

	_setupCountries() {
		if (window.Shopify && window.Shopify.CountryProvinceSelector) {
			// eslint-disable-next-line no-new
			new window.Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
				hideElement: 'AddressProvinceContainerNew',
			})
		} else {
			new CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
				hideElement: 'AddressProvinceContainerNew',
			})
		}

		this.elements.countrySelects?.forEach((select) => {
			const formId = select.dataset.formId
			if (window.Shopify && window.Shopify.CountryProvinceSelector) {
				new window.Shopify.CountryProvinceSelector(
					`AddressCountry_${formId}`,
					`AddressProvince_${formId}`,
					{
						hideElement: `AddressProvinceContainer_${formId}`,
					}
				)
			} else {
				new CountryProvinceSelector(
					`AddressCountry_${formId}`,
					`AddressProvince_${formId}`,
					{
						hideElement: `AddressProvinceContainer_${formId}`,
					}
				)
			}
			// eslint-disable-next-line no-new
		})
	}

	_setupEventListeners() {
		this.elements.cancelButtons?.forEach((element: HTMLElement) => {
			element.addEventListener('click', this._handleCancelButtonClick)
		})
		this.elements.deleteButtons?.forEach((element: HTMLElement) => {
			element.addEventListener('click', this._handleDeleteButtonClick)
		})
	}

	_toggleExpanded(target: HTMLElement) {
		target.setAttribute(
			attributes.expanded,
			(target.getAttribute(attributes.expanded) === 'false').toString()
		)
	}

	_handleCancelButtonClick = (event: MouseEvent | TouchEvent) => {
		this._toggleExpanded(
			qsRequired(
				`[${attributes.expanded}]`,
				closestRequired(currentTargetRequired(event), selectors.addressContainer)
			)
		)
	}

	_handleDeleteButtonClick = (event: MouseEvent | TouchEvent) => {
		const currentTarget = currentTargetRequired(event)
		const confirmMessage = currentTarget.getAttribute(attributes.confirmMessage) ?? undefined
		if (confirm(confirmMessage)) {
			const link = currentTarget.dataset.target
			if (!link) throw 'no link on _handleDeleteButtonClick'
			window.Shopify.postLink(link, {
				parameters: { _method: 'delete' },
			})
		}
	}
}

export class PasswordField extends UcoastEl {
	static htmlSelector = 'password-field'
	input: HTMLInputElement
	button: HTMLButtonElement
	constructor() {
		super()
		this.button = qsRequired('button', this)
		this.input = qsRequired('input', this)
		this.button.addEventListener('click', (event: Event) => {
			event.preventDefault()
			if (!(event instanceof MouseEvent)) return
			if (event.detail !== 1) return
			const reveal = !this.hasAttribute('data-uc-pw-revealed')
			PasswordField.toggleVisibility(reveal)
			this.input.focus()
		})
	}

	static toggleVisibility(reveal: boolean) {
		const allInstances = qsaRequired<PasswordField>('password-field')
		allInstances.forEach((instance) => {
			if (reveal) {
				instance.setAttribute('data-uc-pw-revealed', '')
				instance.input.setAttribute('type', 'text')
			} else {
				instance.removeAttribute('data-uc-pw-revealed')
				instance.input.setAttribute('type', 'password')
			}
		})
	}
}

export class AccountToggle extends UcoastEl {
	static htmlSelector = 'account-toggle'
	elements: {
		toggleButtons: NodeListOf<HTMLButtonElement>
		content: HTMLElement
	}
	constructor() {
		super()
		this.elements = this.getElements()
	}

	override connectedCallback() {
		super.connectedCallback()
		this.init()
	}
	init() {
		this.elements = this.getElements()
		this.elements.toggleButtons.forEach((button) => {
			button.addEventListener('click', this.expandShowMore.bind(this))
		})
	}
	getElements() {
		const toggleButtons = qsaRequired<HTMLButtonElement>(
			'button[data-uc-account-toggle-button]',
			this
		)
		const content = qsRequired('[data-uc-account-toggle-content]', this)
		return {
			toggleButtons,
			content,
		}
	}
	expandShowMore(event: MouseEvent | TouchEvent) {
		event.preventDefault()
		const currentTarget = currentTargetRequired(event)
		currentTarget.classList.add('opacity-0')
		if (this.hasAttribute('open')) {
			this.elements.content.style.maxHeight = `0px`
			this.elements.toggleButtons.forEach((button) => {
				button.setAttribute('aria-expanded', 'false')
				currentTarget.classList.remove('opacity-0')
			})
			window.setTimeout(() => {
				this.removeAttribute('open')
			}, 300)
		} else {
			this.setAttribute('open', '')
			this.elements.toggleButtons.forEach((button) => {
				button.setAttribute('aria-expanded', 'true')
			})
			this.elements.content.style.maxHeight = `${this.elements.content.scrollHeight}px`
		}
	}
}
